/*
  HANNE BLOCH quick view, the "Mini PDP" (Vic). Its own file since HB 2026-10-15 (QV round 1, fix 5.150, Vic: "Yes, move
  it first"). Until then it lived in assets/hb-sheet.js beside the Silk Tulle Set builder and the silk story reel, and
  two sessions changed that one file round after round. The sheet itself stays there (snippets/hb-sheet, the <dialog>;
  open, close, the head and its drag, the page lock, the one history entry, blocked()) and is reached here through
  window.HBSheet; this file owns the card tap and everything inside the quick view. Styles: assets/hb-qv.css (the
  sheet's own rules stay in assets/hb-sheet.css). Loaded by sections/main-collection.liquid right after hb-sheet.js,
  only while Theme settings > Collection pages: the quick view is on; never for B2B (the loader, the snippet and
  sections/hb-qv all leave them out).

  What it does (HB 2026-10-13, round 41, fix 5.88, the spec 03_Operations/Shopify/Theme_Merge/
  261013_HB_theme_spec_collection_slideups_v1.md; rounds 44 to 46): the first tap on a card in the grid opens the quick
  view instead of the product page: the pictures zoomed in (a rail, the next one peeking, thumbnails; it loops, round
  45), the name and price, the color dots (the card's siblings, at most eight, then +n to the product, round 44), the
  sizes (M first, else the nearest in stock, her remembered size when in stock; Short before Regular), ADD TO CART and
  the price (the old price struck first, Vic's pick C1, round 46) and "Take me to the product". An add closes the
  sheet; the quick add's own note rises ("Added to your cart", VIEW CART) and she keeps browsing. The "+" on the card
  is untouched (still the quick add). Phone: the whole screen; desktop: a centered window (Theme settings > Collection
  pages: the quick view, "Also on desktop").

  HB 2026-10-15 (QV round 2, fix 5.151, Vic's first round of notes):
  - "Size guide & info" no longer takes the whole sheet with a Back and a "DETAILS" title: the product page's own tab
    sheet (DESCRIPTION / FIT / CARE / DELIVERY, the x on their line) slides up over the Mini PDP, which stays as she
    left it; its x, a swipe down, the dim, Esc and the phone's Back close it back to the Mini PDP (the Back through
    S.onBack, a hook in assets/hb-sheet.js's Back handler). Fetched ahead once the quick view is in, so it opens
    complete.
  - The rise: the sheet comes up with the Mini PDP's own frame in place (the card's picture in the first picture's
    exact place and size, its name, price, colors and sizes), never moves anything while it rises, and the content
    takes over with a short crossfade once it is up and its first picture is ready (it used to come in mid-rise in a
    picture 16 px wider, which snapped left at the end). A quick view fetched in the last three minutes (back to the
    same piece; on desktop a card hovered for a moment) rises complete. The quick view's own curve is in hb-qv.css.
  - A color dot crossfades to the sibling the same way, and ADD TO CART waits while the sibling comes.

  HB 2026-10-15 (QV round 3, fix 5.152, Vic): no "Color:" and "Size:" before the rows (sections/hb-qv; Top and Bottom
  keep their names), "NEVER ON SALE –" to the left of the price. The frame the sheet rises with follows the same rules.

  HB 2026-10-15 (QV round 4, Vic's third notes):
  - The color dots eight at a time: "+n" slides the row to the next colors and stands at the other end for the way
    back (fix 5.153; dotsSetup and the rest below). Replaces round 3's "up to ten".
  - Colors ready before she taps them (fix 5.154, Vic: "remove or minimize the lag ... IT SHOULD NEVER SLOW DOWN THE
    EXPERIENCE FOR THE USER"): the colors in view are fetched quietly once the quick view has its own pictures (each
    color's quick view and first picture), a finger on a dot fetches that one at once, and a tap starts the picture
    together with the quick view (aheadSee and the rest below).
  - Found on the way: a fetched quick view is now taken into this page before its first picture is awaited (parseQv).
    Inside the parsed document the picture never loaded and its decode() failed at once, so since round 2 the content
    took over without waiting for its picture, and a color could show an empty frame for a moment.

  Content: sections/hb-qv.liquid through the Section Rendering API (/products/<handle>?section_id=hb-qv), so the price
  follows her market and the sizes follow the quick add's rules; the tabs: sections/hb-qv-fit.liquid (the product
  page's details snippet in an inert template, the panels lifted out of it).

  If an older assets/hb-sheet.js still carries its own copy of the quick view (window.HBSheet.openQuickView is there
  already), this file stays out of the way, so the card never opens two quick views.

  HB 2026-10-16 (QV round 5, Vic's answers on the Mini PDP against the product page, "only if you can fit it in
  nicely; it is important we do not over-crowd this page"): "% OFF" after a sale price (per variant); "Fits XS – XXL"
  beside One Size (sections/hb-qv.liquid); a preorder piece's note on the order line (properties[Preorder]); the
  Delivery tab's Arrives line filled from the cart's delivery date (fillArrival); a STYLING tab drafted in
  sections/hb-qv-fit (names and the cart's lines only, until the pairs carry products). The trust line stays out.
*/
import { CartUpdateEvent } from '@theme/events';

const QV_VERSION = '2026-10-23qv7';
const DESKTOP = 750;
// The quick view's own rise in assets/hb-qv.css (0.42 s on a phone, 0.34 s on desktop); nothing heavy runs before it ends
const RISE_PHONE = 440;
const RISE_DESKTOP = 360;
const QV_TTL = 180000; // a fetched quick view is used again for three minutes
const DOTS_MAX = 8; // color dots in view at a time (QV round 4, Vic: "Keep it to 8 maximum swatches")

// The sheet, from assets/hb-sheet.js (window.HBSheet), set in init(): its open and close, its state and its timing
let H = null;
let S = null;
let open = null;
let close = null;
let SHEET_MS = 350;
let blocked = () => false; // the sheet's own test (the cart, a quick add or its note, the gift note or its slide-up), set in init()
let riseEnd = 0;

const dlg = () => document.querySelector('dialog.hb-sl');
const q = (sel, root) => (root || dlg()).querySelector(sel);
const phone = () => window.innerWidth < DESKTOP;
const wait = (ms) => new Promise((res) => setTimeout(res, ms));

// The head's Back and title belong to the sheet (assets/hb-sheet.js); the quick view only asks for them
function setHead(opts) {
  if (H && typeof H.setHead === 'function') H.setHead(opts);
}

function track(name, data) {
  try {
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(name, data || {});
    }
  } catch (e) {
    /* analytics never breaks the sheet */
  }
  try {
    if (typeof window.clarity === 'function') window.clarity('event', name);
  } catch (e) {
    /* nothing */
  }
}

function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// ---------------------------------------------------------------------------------------------------------------
// The quick view

// ---------------------------------------------------------------------------------------------------------------
// The pack shot framed for its kind of piece (HB 2026-10-21, Vic: "please code this with just a bit of movement up or
// down and light zoom between 0% and maximum 30% depending on the product": skirts and trousers up with less zoom,
// tops down with medium zoom, high briefs up with the most zoom, the Adjustable Top and the Bandeau Top down with the
// most zoom, sets as they are). Only the first picture, and only when it is the pack shot (a .png or .webp file; the
// photographs on a model are .jpg, and the second picture can be a model in a .png, so it is the first one only), in
// the frame the sheet rises with, under the first picture while it comes and in the quick view itself. The kind is
// read from the piece's name, so a new product needs nothing. The values are in hb-qv.css ([data-hb-frame]).
// Measured on the pack shots of 21 Oct (every one a 2:3 picture cut out on a clear ground), some pieces sit apart from
// the rest of their kind, so they have their own: the Halter Top reaches lower than the Silk Tulle and String Silk
// Tops, so it moves down less and is zoomed a little less; the Knit Belted Top fills the picture (its belt ends reach
// the foot), so it is not zoomed or moved, only centered; the Adjustable Top sits higher than the Bandeau Top, so it
// moves down further. FRAME_AS_IS: a pack shot laid out unlike the rest of its kind stays as it is (the Taupe
// Adjustable Top's ties hang down to the foot of its picture; framed as the other Adjustable Tops, they would be cut).
const FRAME_AS_IS = { 'taupe-adjustable-top': 1 };
function frameKind(title, handle) {
  if (handle && FRAME_AS_IS[handle]) return '';
  const t = ' ' + String(title || '').toLowerCase().replace(/[^a-z]+/g, ' ') + ' ';
  if (/ sets? /.test(t)) return ''; // a set: as it is
  if (/ high briefs? /.test(t)) return 'brief';
  if (/ adjustable tops? /.test(t)) return 'adjustable';
  if (/ bandeau tops? /.test(t)) return 'bandeau';
  if (/ (skirts?|trousers|pants) /.test(t)) return 'bottom';
  if (/ belted tops? /.test(t)) return 'belted';
  if (/ halter tops? /.test(t)) return 'halter';
  if (/ tops? /.test(t)) return 'top';
  return '';
}
const isPackShot = (src) => /\.(png|webp)(\?|#|$)/i.test(String(src || '').trim().split(/[\s,]/)[0]);
function frameQv(qv) {
  const kind = frameKind(qv.dataset.title || '', qv.dataset.handle || '');
  if (!kind) return;
  const first = qv.querySelector('[data-hb-qv-slide]');
  const img = first && first.querySelector('img');
  if (!img || !isPackShot(img.getAttribute('src') || img.getAttribute('srcset'))) return;
  first.dataset.hbFrame = kind;
  const loop = qv.querySelector('[data-hb-qv-loop]');
  if (loop) loop.dataset.hbFrame = kind;
}

function cardName(card) {
  const el = card.querySelector('.hb-name, [class*="product-card__title"], [class*="title"]');
  return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
}

// the picture the card shows now (its first slide; the hidden model or product view is passed over)
function cardPicture(card) {
  if (!card) return '';
  const imgs = [...card.querySelectorAll('.card-gallery img')];
  const shown = imgs.find((img) => img.offsetWidth > 0 && (img.currentSrc || img.src)) || imgs[0] || card.querySelector('img');
  return shown ? shown.currentSrc || shown.src || '' : '';
}

function cardHandle(card) {
  const link = card && card.querySelector('.product-card__link');
  if (!link || !link.getAttribute('href')) return '';
  try {
    const m = new URL(link.href, location.href).pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  } catch (e) {
    return '';
  }
}

// The fetched quick views, kept for three minutes (the HTML; a fresh element is parsed from it for every use)
const qvCache = new Map();
function qvText(handle) {
  const now = Date.now();
  const hit = qvCache.get(handle);
  if (hit && now - hit.at < QV_TTL) return hit.p;
  const entry = { at: now, text: null, p: null };
  entry.p = fetch('/products/' + encodeURIComponent(handle) + '?section_id=hb-qv', { credentials: 'same-origin' })
    .then((res) => {
      if (!res.ok) throw new Error('hb-qv ' + res.status);
      return res.text();
    })
    .then((text) => {
      entry.text = text;
      return text;
    });
  entry.p.catch(() => {
    if (qvCache.get(handle) === entry) qvCache.delete(handle);
  });
  qvCache.set(handle, entry);
  if (qvCache.size > 30) qvCache.delete(qvCache.keys().next().value);
  return entry.p;
}
function readyText(handle) {
  const hit = qvCache.get(handle);
  return hit && hit.text && Date.now() - hit.at < QV_TTL ? hit.text : null;
}
function parseQv(html) {
  if (!html) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const el = doc.querySelector('[data-hb-qv]');
  if (!el) return null;
  // Into this page's document (not yet into the page), its first picture asked for at once: only here can that picture
  // load and be decoded before it shows; in the parsed document it never loads and its decode() fails at once (QV
  // round 4). Every quick view parsed here is shown.
  frameQv(el); // HB 2026-10-21: the pack shot framed for its kind of piece
  const first = el.querySelector('[data-hb-qv-slide] img');
  if (first) {
    first.loading = 'eager';
    try {
      first.fetchPriority = 'high';
    } catch (e) {
      /* older browsers */
    }
  }
  return document.adoptNode(el);
}

// The frame the sheet rises with: the quick view's own layout, filled with what the card already shows (its picture,
// name and price, its color swatches, the sizes of its quick add), so the picture sits where the quick view's first
// picture will sit and nothing moves when the content comes (QV round 2, Vic: the picture "snaps left")
const RANK = ['M', 'L', 'S', 'XL', 'XS', 'XXL', 'XXS', 'XXXL', '3XL'];
function rememberedSize() {
  try {
    return String(localStorage.getItem('hb-size') || '').trim().toUpperCase();
  } catch (e) {
    return '';
  }
}
function pickPill(pills) {
  const inStock = pills.filter((p) => !p.out && p.v.length <= 4);
  const pref = rememberedSize();
  const mine = pref && inStock.find((p) => p.v.toUpperCase() === pref);
  if (mine) return mine.v;
  for (const r of RANK) {
    const hit = inStock.find((p) => p.v.toUpperCase() === r);
    if (hit) return hit.v;
  }
  return '';
}
function waitParts(card) {
  const parts = { pic: '', name: '', price: '', compare: '', note: '', dots: [], more: 0, rows: [], thumbs: 1 };
  if (!card) return parts;
  try {
    parts.pic = cardPicture(card);
    parts.name = cardName(card);
    const pp = card.querySelector('product-price');
    if (pp) {
      const monies = [...pp.querySelectorAll('.money')];
      const struck = monies.find((m) => m.closest('s, del, .compare-at-price, .price__compare'));
      const now = monies.filter((m) => m !== struck).pop();
      parts.price = (now || pp).textContent.replace(/\s+/g, ' ').trim();
      parts.compare = struck ? struck.textContent.replace(/\s+/g, ' ').trim() : '';
    }
    const badge = [...card.querySelectorAll('[class*="badge"]')].map((b) => b.textContent.replace(/\s+/g, ' ').trim()).find((t) => /never on sale/i.test(t));
    if (badge) parts.note = 'NEVER ON SALE –';
    const seen = new Set();
    card.querySelectorAll('card-swatch img, .swatch img').forEach((img) => {
      const src = img.currentSrc || img.src;
      if (!src || seen.has(src)) return;
      seen.add(src);
      if (parts.dots.length < DOTS_MAX) parts.dots.push(src);
      else parts.more += 1; // the "+n" of the first eight (QV round 4)
    });
    card.querySelectorAll('.hb-qa__sel').forEach((row) => {
      const lab = ((row.querySelector('.hb-qa__optname') || {}).textContent || 'Size:').trim();
      const pills = [...row.querySelectorAll('.hb-qa__pill')]
        .map((p) => ({ v: p.textContent.replace(/\s+/g, ' ').trim(), out: p.disabled || /(^|\s)(is-out|is-sold|sold-out|disabled)(\s|$)/.test(p.className) }))
        .filter((p) => p.v);
      if (pills.length) parts.rows.push({ lab, pills });
    });
    parts.thumbs = Math.max(1, Math.min(8, card.querySelectorAll('.card-gallery slideshow-slide').length));
  } catch (e) {
    /* the frame then shows less */
  }
  return parts;
}
// A name breaks at a space, never inside a hyphenated word ("Sample - Black Glitter" / "V-Swimsuit", not
// "... V-" / "Swimsuit"; QV round 4): such words are kept whole in a span (assets/hb-qv.css, .hb-qv__nb)
function keepWords(el) {
  if (!el) return;
  const walk = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const found = [];
  while (walk.nextNode()) {
    if (/\S-\S/.test(walk.currentNode.nodeValue)) found.push(walk.currentNode);
  }
  found.forEach((node) => {
    const frag = document.createDocumentFragment();
    node.nodeValue.split(/(\S+-\S+)/).forEach((part, i) => {
      if (!part) return;
      if (i % 2) {
        const span = document.createElement('span');
        span.className = 'hb-qv__nb';
        span.textContent = part;
        frag.appendChild(span);
      } else {
        frag.appendChild(document.createTextNode(part));
      }
    });
    node.parentNode.replaceChild(frag, node);
  });
}

function waitMarkup(parts) {
  const waitFrame = parts.pic && isPackShot(parts.pic) ? frameKind(parts.name, parts.handle) : ''; // framed as the quick view's first picture will be
  const img = (src, cls) => (src ? '<img' + (cls ? ' class="' + cls + '"' : '') + ' src="' + esc(src) + '" alt="">' : '');
  const thumbs = Array.from({ length: parts.thumbs }, (x, i) => '<span class="hb-qv__thumb' + (i === 0 ? ' is-on' : '') + '">' + (i === 0 ? img(parts.pic) : '') + '</span>').join('');
  const price = (parts.note ? '<span class="hb-qv__note">' + esc(parts.note) + '</span>' : '') + (parts.compare ? '<s>' + esc(parts.compare) + '</s>' : '') + '<span>' + esc(parts.price) + '</span>';
  const more = parts.more ? '<span class="hb-qv__page" style="left:' + (2 + DOTS_MAX * 32) + 'px">+' + parts.more + '</span>' : '';
  const dots = parts.dots.length ? '<div class="hb-qv__row"><div class="hb-qv__dots"><div class="hb-qv__track">' + parts.dots.map((src) => '<span class="hb-qv__dot">' + img(src) + '</span>').join('') + '</div>' + more + '</div></div>' : '';
  const sg = '<span class="hb-qv__sg">Size guide &amp; info</span>';
  let rows = '';
  if (parts.rows.length) {
    rows = parts.rows
      .map((r, i) => {
        const pick = pickPill(r.pills);
        const pills = r.pills.map((p) => '<span class="hb-qv__pill' + (p.v.length > 3 ? ' hb-qv__pill--word' : '') + (p.v === pick ? ' is-on' : '') + (p.out ? ' is-out' : '') + '"><span>' + esc(p.v) + '</span></span>').join('');
        const lab = /^(size|length|colou?r)\b/i.test(r.lab) ? '' : '<span class="hb-qv__lab">' + esc(r.lab) + '</span>'; // Top and Bottom keep their names (round 3)
        return '<div class="hb-qv__row">' + lab + '<div class="hb-qv__pills">' + pills + '</div>' + (i === 0 ? sg : '') + '</div>';
      })
      .join('');
  } else {
    rows = '<div class="hb-qv__row"><div class="hb-qv__pills"><span class="hb-qv__pill hb-qv__pill--word is-on"><span>One Size</span></span></div>' + sg + '</div>';
  }
  return (
    '<div class="hb-qv hb-qv--wait" aria-hidden="true">' +
    '<div class="hb-qv__pics"><div class="hb-qv__rail"><div class="hb-qv__slide"' + (waitFrame ? ' data-hb-frame="' + waitFrame + '"' : '') + '>' + (img(parts.pic, 'hb-qv__img') || '<span></span>') + '</div><div class="hb-qv__slide"><span></span></div></div>' +
    '<div class="hb-qv__thumbs">' + thumbs + '</div></div>' +
    '<div class="hb-qv__details"><div class="hb-qv__head"><span class="hb-qv__name">' + esc(parts.name).replace(/(\S+-\S+)/g, '<span class="hb-qv__nb">$1</span>') + '</span><span class="hb-qv__price">' + price + '</span></div>' + dots + rows + '</div>' +
    '</div>'
  );
}
function waitFoot(parts) {
  const price = parts.price ? '<i class="hb-qv__sep" aria-hidden="true"></i><span class="hb-qv__addp">' + (parts.compare ? '<s>' + esc(parts.compare) + '</s> ' : '') + esc(parts.price) + '</span>' : '';
  return '<div class="hb-qv__foot" aria-hidden="true"><span class="hb-qv__add"><span>Add to cart</span>' + price + '</span><span class="hb-qv__to">Take me to the product</span></div>';
}

function openQuickView(handle, url, card, opener) {
  const ready = readyText(handle);
  if (!open('product', opener)) return;
  const d = dlg();
  d.setAttribute('aria-label', 'Quick view');
  S.handle = handle;
  S.url = url;
  S.card = card;
  dotsKeep = null; // a new piece: its color row opens on its own color
  dotsLead = handle; // and that color leads the row (HB 2026-10-23)
  riseEnd = performance.now() + (phone() ? RISE_PHONE : RISE_DESKTOP);
  setHead({});
  const body = q('[data-hb-sl-body]', d);
  const foot = q('[data-hb-sl-foot]', d);
  // Fetched a moment ago: the quick view itself rises (the card's picture under its first picture until that is in)
  const qv = ready ? parseQv(ready) : null;
  if (qv) {
    ++S.req;
    body.innerHTML = '';
    dress(qv);
    mountQv(qv);
    return;
  }
  const parts = waitParts(card);
  parts.handle = handle; // (the framing's exceptions go by the piece)
  body.innerHTML = waitMarkup(parts);
  foot.innerHTML = waitFoot(parts);
  loadProduct(handle, true);
}

// the card's picture under the quick view's first picture until its own is in (never a grey frame; it goes the moment
// the picture has come, so a cut-out picture never shows the card's picture through it), the thumbnails at once
function dress(qv) {
  const pic = S.card ? cardPicture(S.card) : '';
  const slide = qv.querySelector('[data-hb-qv-slide]');
  const img = slide && slide.querySelector('img');
  if (pic && img && slide.dataset.hbFrame && isPackShot(pic)) {
    // HB 2026-10-21: a framed first picture: the card's picture under it as a picture framed the same way (a background
    // could not be framed), gone the moment the picture has come. Only when the card shows the pack shot too: a card
    // that shows a film (the Bandeau and Adjustable Tops) or a model goes under it as it is, as before
    const under = document.createElement('img');
    under.className = 'hb-qv__img hb-qv__under';
    under.alt = '';
    under.setAttribute('aria-hidden', 'true');
    under.src = pic;
    slide.insertBefore(under, slide.firstChild);
    const clear = () => under.remove();
    if (img.complete && img.naturalWidth) clear();
    else {
      img.addEventListener('load', clear, { once: true });
      img.addEventListener('error', clear, { once: true });
    }
  } else if (pic && img) {
    slide.style.backgroundImage = 'url("' + pic.replace(/"/g, '%22') + '")';
    slide.classList.add('hb-qv__slide--under');
    const clear = () => {
      slide.style.backgroundImage = '';
      slide.classList.remove('hb-qv__slide--under');
    };
    if (img.complete && img.naturalWidth) clear();
    else {
      img.addEventListener('load', clear, { once: true });
      img.addEventListener('error', clear, { once: true });
    }
  }
  qv.querySelectorAll('[data-hb-qv-thumb] img').forEach((img, i) => {
    if (i < 8) img.loading = 'eager';
  });
}

// the first picture, loaded and decoded before it shows (at most 0.9 s, then it shows as it comes)
function firstPicture(qv) {
  const img = qv.querySelector('[data-hb-qv-slide] img');
  if (!img) return Promise.resolve();
  img.loading = 'eager';
  try {
    img.fetchPriority = 'high';
  } catch (e) {
    /* nothing */
  }
  if (typeof img.decode !== 'function') return Promise.resolve();
  return Promise.race([img.decode().catch(() => {}), wait(900)]);
}

function untilRisen() {
  const left = riseEnd - performance.now();
  return left > 0 ? wait(left) : Promise.resolve();
}

async function loadProduct(handle, first) {
  const my = ++S.req;
  let qv = null;
  try {
    qv = parseQv(await qvText(handle));
  } catch (e) {
    qv = null;
  }
  if (my !== S.req || !S.open) return;
  if (!qv) {
    // Nothing to show (the section refused, or a network miss): the product page itself, as before.
    const to = S.url || '/products/' + handle;
    close();
    setTimeout(() => {
      location.href = to;
    }, 50);
    return;
  }
  if (first) dress(qv);
  // never while the sheet still rises (the rise stays smooth), and with its first picture ready
  await Promise.all([untilRisen(), firstPicture(qv)]);
  if (my !== S.req || !S.open) return;
  S.handle = handle;
  mountQv(qv);
}

function mountQv(qv) {
  const d = dlg();
  const body = q('[data-hb-sl-body]', d);
  const foot = q('[data-hb-sl-foot]', d);
  const panel = q('[data-hb-sl-panel]', d);
  dropInfo(); // a Size guide & info sheet of the piece before goes with it
  // What showed until now (the frame the sheet rose with, or the color before) stays where it is, over the new
  // content, and fades: a crossfade instead of a jump
  let ghost = body.querySelector(':scope > .hb-qv');
  if (ghost && panel) {
    const pr = panel.getBoundingClientRect();
    const gr = ghost.getBoundingClientRect();
    if (gr.width > 0 && gr.height > 0) {
      ghost.removeAttribute('data-hb-qv');
      ghost.setAttribute('aria-hidden', 'true');
      ghost.inert = true;
      ghost.classList.add('hb-qv--ghost');
      ghost.style.left = gr.left - pr.left + 'px';
      ghost.style.top = gr.top - pr.top + 'px';
      ghost.style.width = gr.width + 'px';
      ghost.style.height = gr.height + 'px';
    } else {
      ghost = null;
    }
  } else {
    ghost = null;
  }
  [...body.children].forEach((c) => {
    if (c !== ghost) c.remove();
  });
  foot.innerHTML = '';
  foot.hidden = false;
  const footEl = qv.querySelector('[data-hb-qv-foot]');
  if (footEl) foot.appendChild(footEl);
  body.insertBefore(qv, body.firstChild);
  body.scrollTop = 0;
  setHead({});
  wireQv(qv, foot);
  if (ghost) {
    setTimeout(() => ghost.classList.add('is-gone'), 20);
    setTimeout(() => ghost.remove(), 300);
  }
  // Size guide & info fetched ahead, so it opens complete
  const handle = S.handle;
  setTimeout(() => {
    if (S.open && S.handle === handle) infoEntry(handle);
  }, 800);
  // then, quietly, the other colors in view (QV round 4)
  aheadStart(qv);
}

function variantsOf(qv) {
  try {
    return JSON.parse(qv.querySelector('[data-hb-qv-variants]').textContent) || [];
  } catch (e) {
    return [];
  }
}

function selection(qv) {
  const sel = {};
  qv.querySelectorAll('[data-hb-qv-opt]').forEach((row) => {
    const on = row.querySelector('.hb-qv__pill.is-on');
    sel[row.dataset.hbQvOpt] = on ? on.dataset.value : null;
  });
  return sel;
}

function matchVariant(variants, sel) {
  const keys = Object.keys(sel);
  return variants.find((v) => keys.every((k) => sel[k] == null || v.o[Number(k) - 1] === sel[k])) || null;
}

function paintQv(qv, foot) {
  const variants = variantsOf(qv);
  const sel = selection(qv);
  // A value is out when no variant in stock carries it with the other chosen values
  qv.querySelectorAll('[data-hb-qv-opt]').forEach((row) => {
    const pos = Number(row.dataset.hbQvOpt);
    row.querySelectorAll('.hb-qv__pill').forEach((pill) => {
      const other = Object.assign({}, sel);
      other[String(pos)] = pill.dataset.value;
      const any = variants.some((v) => v.a && Object.keys(other).every((k) => k === String(pos) || other[k] == null || v.o[Number(k) - 1] === other[k]) && v.o[pos - 1] === pill.dataset.value);
      pill.classList.toggle('is-out', !any);
      if (!any) pill.setAttribute('aria-label', pill.dataset.value + ', sold out');
      else pill.removeAttribute('aria-label');
    });
  });
  const v = matchVariant(variants, sel);
  const add = foot.querySelector('[data-hb-qv-add]');
  const price = qv.querySelector('[data-hb-qv-amount]');
  const compare = qv.querySelector('[data-hb-qv-compare]');
  const addPrice = foot.querySelector('[data-hb-qv-add-price]');
  const addTx = foot.querySelector('[data-hb-qv-add-tx]');
  const sep = foot.querySelector('.hb-qv__sep');
  if (!add) return;
  if (v) {
    add.dataset.variantId = String(v.id);
    if (price) price.innerHTML = v.p; // the shop's money format wraps the amount in a span; our own Liquid wrote it
    if (compare) {
      compare.innerHTML = v.c || '';
      compare.hidden = !v.c;
    }
    if (addPrice) addPrice.innerHTML = (v.c ? '<s>' + v.c + '</s> ' : '') + v.p; // the old price struck first, same size (Vic's pick C1, round 46)
    // HB 2026-10-16 (Mini PDP item 1): "% OFF" after a sale price, per variant, as the product page's price row
    const off = qv.querySelector('[data-hb-qv-off]');
    if (off) {
      off.textContent = v.off || '';
      off.hidden = !v.off;
    }
    // (item 4) a preorder variant: its note goes onto the order line (addToCart)
    if (v.pre) add.dataset.pre = '1';
    else delete add.dataset.pre;
    if (v.a) {
      add.disabled = false;
      if (addTx) addTx.textContent = v.pre && add.dataset.preorder ? add.dataset.preorder : 'Add to cart';
      if (sep) sep.hidden = false;
      if (addPrice) addPrice.hidden = false;
    } else {
      add.disabled = true;
      if (addTx) addTx.textContent = 'Sold out';
      if (sep) sep.hidden = true;
      if (addPrice) addPrice.hidden = true;
    }
  } else {
    add.disabled = true;
    if (addTx) addTx.textContent = 'Choose a size';
    if (sep) sep.hidden = true;
    if (addPrice) addPrice.hidden = true;
  }
}

function applyRemembered(qv) {
  // Her remembered size (the product page's rule) when that size is in stock; only on a letter-size option
  const pref = rememberedSize();
  if (!pref) return;
  const variants = variantsOf(qv);
  qv.querySelectorAll('[data-hb-qv-opt]').forEach((row) => {
    const pos = Number(row.dataset.hbQvOpt);
    const pill = [...row.querySelectorAll('.hb-qv__pill')].find((p) => p.dataset.value.toUpperCase() === pref);
    if (!pill) return;
    const inStock = variants.some((v) => v.a && v.o[pos - 1] === pill.dataset.value);
    if (!inStock) return;
    row.querySelectorAll('.hb-qv__pill').forEach((p) => {
      p.classList.toggle('is-on', p === pill);
      p.setAttribute('aria-pressed', p === pill ? 'true' : 'false');
    });
  });
}

// ---------------------------------------------------------------------------------------------------------------
// The color dots, eight at a time (QV round 4, fix 5.153, Vic: "Keep it to 8 maximum swatches. If a user presses the
// +n, then let the swatches slide left so that n swatches are hidden on left side and have +n there instead. And then
// show the new n swatches on right side as they slid into view."). sections/hb-qv sends every color in stock, each on
// one track. At most eight show; a "+n" at either end counts the colors beyond it and slides the row that way: the
// dots that leave fade as they go, the new ones fade in as they come, the "+n" moves to the end she came from
// (assets/hb-qv.css). Twelve colors: [1 to 8] +4, then +4 [5 to 12]. More than sixteen: eight at a time, a "+n" at
// each end in between, and "+n" back retraces her steps. The row opens on the window holding her color, and keeps its
// place when she picks a color in it. A phone too narrow for eight and a "+n" shows fewer, never a second line.
let dotsKeep = null; // { s, back }: the row's place, carried into the color she picks
// HB 2026-10-23 (Vic: "White first is not the house order ... for standard I would recommend that her color shows
// first, and there is no 'house order'"): the color she opened the Mini PDP on leads the row and the others follow in
// the group's order, as on the card she tapped, so the row the sheet rises with is the row that stays. The order holds
// while she tries other colors (each color's quick view is put in the same order), so no dot moves under her finger.
let dotsLead = '';
function dotsOrder(qv) {
  const track = qv.querySelector('[data-hb-qv-track]');
  if (!track || !dotsLead) return;
  const lead = [...track.querySelectorAll('[data-hb-qv-color]')].find((d) => d.dataset.hbQvColor === dotsLead);
  if (lead && lead !== track.firstElementChild) track.insertBefore(lead, track.firstElementChild);
}

function dotsGeo(box) {
  const track = box && box.querySelector('[data-hb-qv-track]');
  const dots = track ? [...track.querySelectorAll('[data-hb-qv-color]')] : [];
  if (!dots.length) return null;
  const gap = parseFloat(getComputedStyle(track).columnGap) || 6;
  const slot = (dots[0].offsetWidth || 26) + gap; // a dot and its gap: 32 px
  const room = box.clientWidth - 4; // less the 2 px each side kept for a focus ring
  const slots = room > 0 ? Math.max(3, Math.floor((room + gap) / slot)) : DOTS_MAX + 2;
  const T = dots.length;
  const one = Math.min(DOTS_MAX, slots - 1); // dots in view beside one "+n"
  const g = { box, track, dots, T, slot, one, two: Math.max(1, Math.min(DOTS_MAX, slots - 2)) };
  g.all = T <= Math.min(DOTS_MAX, slots); // all in view, no "+n"
  g.end = g.all ? 0 : T - one; // the last window's first dot
  g.prev = box.querySelector('[data-hb-qv-page="-1"]');
  g.next = box.querySelector('[data-hb-qv-page="1"]');
  return g;
}
// how many dots show from the s-th on: eight beside one "+n", fewer where a "+n" stands at each end and room is short
function dotsCount(g, s) {
  if (g.all) return g.T;
  return s <= 0 || s >= g.end ? g.one : g.two;
}
function dotsNext(g, s) {
  return Math.min(s + dotsCount(g, s), g.end);
}

function dotsPaint(g, s, still) {
  const { box, track, dots, T, slot } = g;
  s = g.all ? 0 : Math.max(0, Math.min(s, g.end));
  const k = dotsCount(g, s);
  const lead = s > 0 ? 1 : 0; // a "+n" before the dots
  if (still) box.classList.add('is-still');
  track.style.transform = 'translate3d(' + (lead - s) * slot + 'px,0,0)';
  dots.forEach((dot, i) => {
    const away = i < s || i >= s + k;
    dot.classList.toggle('is-away', away);
    if (away) {
      dot.setAttribute('aria-hidden', 'true');
      dot.tabIndex = -1;
    } else {
      dot.removeAttribute('aria-hidden');
      dot.removeAttribute('tabindex');
    }
  });
  const page = (btn, n, at, words) => {
    if (!btn) return;
    btn.hidden = false;
    btn.classList.toggle('is-off', n <= 0);
    btn.tabIndex = n > 0 ? 0 : -1;
    if (n <= 0) return; // it fades where it stands
    btn.textContent = '+' + n;
    btn.setAttribute('aria-label', words(n));
    btn.style.left = 2 + at * slot + 'px';
  };
  page(g.prev, s, 0, (n) => (n === 1 ? 'Show the color before these' : 'Show the ' + n + ' colors before these'));
  page(g.next, T - s - k, lead + k, (n) => (n === 1 ? 'Show 1 more color' : 'Show ' + n + ' more colors'));
  box.hbS = s;
  if (still) {
    void box.offsetWidth; // in place without moving; the next change slides
    box.classList.remove('is-still');
  }
}

function dotsSetup(qv) {
  dotsOrder(qv);
  const box = qv.querySelector('[data-hb-qv-dots]');
  const g = dotsGeo(box);
  if (!g) return;
  box.hbBack = [];
  let s = 0;
  if (!g.all) {
    const c = Math.max(0, g.dots.findIndex((d) => d.classList.contains('is-on')));
    const keep = dotsKeep;
    if (keep && keep.s <= g.end && c >= keep.s && c < keep.s + dotsCount(g, keep.s)) {
      s = keep.s; // she picked a color in view: the row stays as it was
      box.hbBack = keep.back.slice();
    } else {
      // the window holding her color, reached the way "+n" reaches it, so "+n" back retraces it
      for (let guard = 0; c >= s + dotsCount(g, s) && guard < 40; guard += 1) {
        box.hbBack.push(s);
        s = dotsNext(g, s);
      }
    }
  }
  dotsKeep = null;
  dotsPaint(g, s, true);
  box.querySelectorAll('[data-hb-qv-page]').forEach((btn) => {
    btn.addEventListener('click', () => dotsPage(qv, Number(btn.dataset.hbQvPage)));
  });
}

function dotsPage(qv, dir) {
  const box = qv.querySelector('[data-hb-qv-dots]');
  const g = dotsGeo(box);
  if (!g || g.all) return;
  const s = box.hbS || 0;
  const back = box.hbBack || (box.hbBack = []);
  let to;
  if (dir > 0) {
    to = dotsNext(g, s);
    if (to === s) return;
    back.push(s);
  } else {
    if (s <= 0) return;
    to = back.length ? back.pop() : Math.max(0, s - g.two);
    if (to >= s) to = Math.max(0, s - g.two);
  }
  const had = document.activeElement;
  dotsPaint(g, to, false);
  // a "+n" that has just gone hands the focus on: to the other "+n", else to the first color in view
  if (had && had.classList && had.classList.contains('is-off') && box.contains(had)) {
    const other = [g.prev, g.next].find((b) => b && b !== had && !b.classList.contains('is-off'));
    const first = g.dots.find((d) => !d.classList.contains('is-away'));
    try {
      (other || first).focus({ preventScroll: true });
    } catch (e) {
      /* nothing */
    }
  }
  aheadSee(qv); // the colors now in view are fetched first
  track('hb_sheet_dots', { handle: S.handle, dir });
}

// a turned phone or a resized window: the row laid out again where it was, without sliding
function dotsRelayout() {
  if (!S || !S.open) return;
  const qv = document.querySelector('dialog.hb-sl [data-hb-qv]');
  const box = qv && qv.querySelector('[data-hb-qv-dots]');
  const g = dotsGeo(box);
  if (!g) return;
  box.hbBack = [];
  dotsPaint(g, box.hbS || 0, true);
}

// ---------------------------------------------------------------------------------------------------------------
// Colors ready before she taps them (QV round 4, fix 5.154, Vic: "When clicking color swatches to see another color of
// the product - can you remove or minimize the lag here? ... Perhaps when a Mini PDP or Main PDP is done loading all
// other relevant elements, can it in the background load the first picture of the swatches that are available to
// click for the user? ... IT SHOULD NEVER SLOW DOWN THE EXPERIENCE FOR THE USER. (Only add convenience and speed)").
// A color used to wait for its quick view (about 0.2 s on a fast line, more on a phone) and only then for its first
// picture (as long again). Now:
// - Once the quick view is in and its own first pictures have come, the colors in view are fetched one at a time,
//   between her moves and at low priority: each color's quick view (14 KB) and its first picture, at the address its
//   slide will ask for (the same widths and sizes, so the browser chooses the same file), so a tap finds both and the
//   color comes in at once.
// - A finger on a dot (or the mouse resting on it) fetches that color straight away, ahead of the rest.
// - The tap starts the picture together with the quick view, never after it.
// - Never on Data Saver or 2G, and no more once a picture fetched ahead shows a line under 2 Mbit/s (a finger on a
//   dot still fetches its own color); nothing while she swipes the pictures, while the page is hidden or once the
//   sheet is closed; each color once, the colors in view only, at most 40 fetches a page. A picture is fetched, never
//   decoded ahead.
const QV_SIZES = '(min-width: 750px) 390px, 85vw'; // the slides' sizes and widths in sections/hb-qv.liquid: change both
const QV_WIDTHS = [500, 700, 900];
const AHEAD_MAX = 40;
const net = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
const pics = new Map(); // the pictures asked for ahead, by address; the browser keeps the files
const ahead = { list: [], busy: false, count: 0, quiet: 0, timer: 0, slow: false };
// Safari tells no page the line's speed or Data Saver, so the first picture fetched ahead measures her line: under
// 2 Mbit/s (a weak signal) nothing more is fetched ahead for the rest of the page
const slowNet = () => ahead.slow || !!(net && (net.saveData || /(^|-)2g$/.test(String(net.effectiveType || ''))));
function lineKbps(im) {
  try {
    const e = im && im.currentSrc ? performance.getEntriesByName(im.currentSrc).pop() : null;
    if (!e || e.transferSize < 30000) return 0; // from the cache, or too small to tell
    const ms = e.responseEnd - (e.requestStart || e.startTime); // from the request, not from its wait in line
    return ms > 0 ? (e.transferSize * 8) / ms : 0;
  } catch (err) {
    return 0;
  }
}

// The slide's own srcset: image_tag keeps only the widths the picture has (never an upscale), and with none left it
// has only its src (the 900 px address)
function picSrcset(base, own) {
  const max = Math.min(QV_WIDTHS[QV_WIDTHS.length - 1], Number(own) || Infinity);
  const ws = QV_WIDTHS.filter((w) => w <= max);
  return ws.map((w) => base.replace(/([?&]width=)\d+/, '$1' + w) + ' ' + w + 'w').join(', ');
}
function getPic(base, prio, own) {
  if (!base || !/[?&]width=\d+/.test(base)) return null;
  let im = pics.get(base);
  if (im) return im;
  im = new Image();
  im.decoding = 'async';
  try {
    im.fetchPriority = prio === 'high' ? 'high' : 'low';
  } catch (e) {
    /* older browsers */
  }
  const set = picSrcset(base, own);
  if (set) {
    im.sizes = QV_SIZES; // sizes before srcset, so the first choice is the right one
    im.srcset = set;
  } else {
    im.src = base;
  }
  pics.set(base, im);
  if (pics.size > 24) pics.delete(pics.keys().next().value); // the oldest let go (the browser's own cache still has it)
  return im;
}
const dotPic = (dot, prio) => getPic(dot.dataset.hbQvPic, prio, dot.dataset.hbQvPicW);
function picDone(im, ms) {
  if (!im || (im.complete && im.naturalWidth)) return Promise.resolve();
  return new Promise((res) => {
    const done = () => {
      clearTimeout(t);
      res();
    };
    const t = setTimeout(done, ms);
    im.addEventListener('load', done, { once: true });
    im.addEventListener('error', done, { once: true });
  });
}

// her moves come first: nothing is fetched ahead for a moment after a swipe through the pictures or a tab opened
function aheadQuiet(ms) {
  ahead.quiet = Math.max(ahead.quiet, performance.now() + (ms || 900));
}
// a finger on a dot, or the mouse resting on it: that color now, ahead of the rest
function aheadNow(dot) {
  if (!dot || dot.classList.contains('is-on') || !dot.dataset.hbQvColor) return;
  dotPic(dot, 'high');
  qvText(dot.dataset.hbQvColor).catch(() => {});
}
// once the quick view's own first pictures have come (at most 4 s), the colors in view
function aheadStart(qv) {
  ahead.list = [];
  const own = [...qv.querySelectorAll('[data-hb-qv-slide] img')].slice(0, 2);
  Promise.race([Promise.all(own.map((im) => picDone(im, 4000))), wait(4000)])
    .then(() => wait(600))
    .then(() => {
      if (S.open && qv.isConnected) aheadSee(qv);
    });
}
// the colors in view go first in line (when the quick view is in, and when a "+n" brings others into view)
function aheadSee(qv) {
  if (!qv || !S.open || slowNet()) return;
  const fresh = [...qv.querySelectorAll('[data-hb-qv-color]:not(.is-on):not(.is-away)')];
  ahead.list = fresh.concat(ahead.list.filter((d) => !fresh.includes(d)));
  aheadPump();
}
function aheadPump() {
  clearTimeout(ahead.timer);
  if (ahead.busy || !S || !S.open || slowNet()) return;
  // what is in hand already goes without a fetch (a color seen before, or fetched for another quick view)
  ahead.list = ahead.list.filter((d) => d.isConnected && !(pics.has(d.dataset.hbQvPic) && readyText(d.dataset.hbQvColor)));
  if (!ahead.list.length || ahead.count >= AHEAD_MAX) return;
  const left = ahead.quiet - performance.now();
  if (document.hidden || left > 0) {
    ahead.timer = setTimeout(aheadPump, Math.max(left, 500) + 50);
    return;
  }
  ahead.busy = true;
  const go = () => {
    const dot = ahead.list.shift();
    if (!dot || !dot.isConnected || !S.open) {
      ahead.busy = false;
      ahead.timer = setTimeout(aheadPump, 50);
      return;
    }
    ahead.count += 1;
    const im = dotPic(dot, 'low');
    Promise.all([picDone(im, 8000), qvText(dot.dataset.hbQvColor).catch(() => {})]).then(() => {
      ahead.busy = false;
      const kbps = lineKbps(im);
      if (kbps && kbps < 2000) ahead.slow = true; // a weak line: the rest waits for her finger
      ahead.timer = setTimeout(aheadPump, 150);
    });
  };
  // between her moves: an idle moment (at most 1.2 s away), else a short timer (Safari has no idle callback)
  if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(go, { timeout: 1200 });
  else setTimeout(go, 150);
}

function wireQv(qv, foot) {
  applyRemembered(qv);
  paintQv(qv, foot);
  dotsSetup(qv);
  keepWords(qv.querySelector('[data-hb-qv-name]'));

  // The rail and the thumbnails
  const rail = qv.querySelector('[data-hb-qv-rail]');
  const thumbs = [...qv.querySelectorAll('[data-hb-qv-thumb]')];
  const slides = [...qv.querySelectorAll('[data-hb-qv-slide]')];
  const loop = qv.querySelector('[data-hb-qv-loop]'); // the first picture once more after the last (Vic, round 45)
  const markThumb = (n) => {
    thumbs.forEach((t) => {
      const on = Number(t.dataset.hbQvThumb) === n;
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  };
  if (rail && slides.length) {
    let raf = 0;
    let settle = 0;
    rail.addEventListener('scroll', () => {
      aheadQuiet(900); // her next pictures first (QV round 4)
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const left = rail.scrollLeft + 1;
        let n = 1;
        slides.forEach((s, i) => {
          if (s.offsetLeft - rail.offsetLeft <= left + s.offsetWidth / 2) n = i + 1;
        });
        if (loop && loop.offsetLeft - rail.offsetLeft <= left + loop.offsetWidth / 2) n = 1; // the copy counts as the first
        markThumb(n);
      });
      if (loop) {
        // Past the last picture she lands on the copy of the first; once the rail rests there it jumps back to the
        // first itself (the same picture in the same place, so nothing moves) and the next swipe goes on from there
        clearTimeout(settle);
        settle = setTimeout(() => {
          const at = loop.offsetLeft - rail.offsetLeft - (phone() ? 16 : 0);
          if (rail.scrollLeft >= at - 2) {
            rail.scrollLeft = 0;
            markThumb(1);
          }
        }, 120);
      }
    }, { passive: true });
    thumbs.forEach((t) => {
      t.addEventListener('click', () => {
        aheadQuiet(900);
        const s = slides[Number(t.dataset.hbQvThumb) - 1];
        if (!s) return;
        rail.scrollTo({ left: s.offsetLeft - rail.offsetLeft - (phone() ? 16 : 0), behavior: 'smooth' });
        markThumb(Number(t.dataset.hbQvThumb));
      });
    });
    // The first two pictures load now, the rest as she swipes
    slides.slice(0, 2).forEach((s) => {
      const img = s.querySelector('img');
      if (img) img.loading = 'eager';
    });
  }

  // The sizes
  qv.querySelectorAll('[data-hb-qv-opt]').forEach((row) => {
    row.addEventListener('click', (e) => {
      const pill = e.target.closest('.hb-qv__pill');
      if (!pill || !row.contains(pill)) return;
      row.querySelectorAll('.hb-qv__pill').forEach((p) => {
        p.classList.toggle('is-on', p === pill);
        p.setAttribute('aria-pressed', p === pill ? 'true' : 'false');
      });
      paintQv(qv, foot);
      // Her size, remembered as on the product page (letter sizes only)
      if (/^(XXS|XS|S|M|L|XL|XXL|XXXL|3XL)$/i.test(pill.dataset.value)) {
        try {
          localStorage.setItem('hb-size', pill.dataset.value.toUpperCase());
        } catch (err) {
          /* nothing */
        }
      }
    });
  });

  // The color dots: the sibling's own quick view takes the sheet, the sheet stays (ADD TO CART waits while it comes).
  // A finger on a dot, or the mouse resting on it, fetches that color at once; the tap starts its picture together
  // with its quick view; the row keeps its place (QV round 4)
  qv.querySelectorAll('[data-hb-qv-color]').forEach((a) => {
    let hover = 0;
    a.addEventListener('pointerdown', () => aheadNow(a), { passive: true });
    a.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse') return;
      clearTimeout(hover);
      hover = setTimeout(() => aheadNow(a), 60);
    });
    a.addEventListener('pointerleave', () => clearTimeout(hover));
    a.addEventListener('click', (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      const handle = a.dataset.hbQvColor;
      if (!handle || handle === S.handle) return;
      aheadNow(a);
      qv.querySelectorAll('[data-hb-qv-color]').forEach((o) => o.classList.toggle('is-on', o === a));
      qv.dataset.switching = '1';
      S.url = a.getAttribute('href') || S.url;
      const box = qv.querySelector('[data-hb-qv-dots]');
      dotsKeep = box ? { s: box.hbS || 0, back: (box.hbBack || []).slice() } : null;
      loadProduct(handle, false);
      track('hb_sheet_color', { handle });
    });
  });

  // Size guide & info: the product page's tab sheet over the quick view (QV round 2, Vic)
  const sg = qv.querySelector('[data-hb-qv-fit]');
  if (sg) sg.addEventListener('click', () => showInfo(qv, foot, sg.dataset.tab || 'fit', sg));

  // Take me to the product: the card's own route, so Back comes back to this card (assets/product-card.js's rule)
  const to = foot.querySelector('[data-hb-qv-to]');
  if (to) {
    to.addEventListener('click', (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      track('hb_sheet_to_product', { handle: S.handle });
      // HB 2026-10-23 (Vic): the product page keeps the row she saw here (snippets/product-swatches.liquid reads it once,
      // within 30 s); without it the page leads with the color she goes to
      try {
        const h = [...qv.querySelectorAll('[data-hb-qv-color]')].map((d) => d.dataset.hbQvColor).filter(Boolean);
        if (h.length) sessionStorage.setItem('hb-dots-order', JSON.stringify({ h, t: Date.now() }));
      } catch (err) {
        /* the page leads with its own color */
      }
      // The sheet's history entry goes first (close pops it), then the page: Back from the product page lands on
      // this card, not on a closed sheet.
      S.navTo = to.href;
      if (!S.hist) {
        rememberCard();
        location.href = to.href;
        return;
      }
      close();
    });
  }

  // Add to cart
  const add = foot.querySelector('[data-hb-qv-add]');
  if (add) add.addEventListener('click', () => addToCart(qv, foot, add));
}

function rememberCard() {
  const card = S.card;
  if (!card || window.Shopify && window.Shopify.designMode) return;
  const link = card.querySelector('.product-card__link');
  const anchor = link && link.getAttribute('id');
  if (!anchor) return;
  try {
    const url = new URL(window.location.href);
    url.hash = anchor;
    const li = card.closest('li');
    if (li && li.dataset.page) url.searchParams.set('page', li.dataset.page);
    history.replaceState({}, '', url.toString());
  } catch (e) {
    /* nothing */
  }
}

async function addToCart(qv, foot, add) {
  if (add.disabled || add.classList.contains('is-busy') || qv.dataset.switching) return;
  const id = Number(add.dataset.variantId);
  if (!id) return;
  add.classList.add('is-busy');
  // The quick add's note names the piece: hand it the name and the picture (assets/hb-cart.js reads state.qaPiece)
  try {
    const img = qv.querySelector('.hb-qv__img');
    const name = qv.querySelector('[data-hb-qv-name]');
    if (window.HBCart && window.HBCart.state) {
      window.HBCart.state.qaPiece = {
        name: name ? name.textContent.replace(/\s+/g, ' ').trim() : qv.dataset.title || '',
        img: img ? img.currentSrc || img.src || '' : qv.dataset.img || '',
        at: performance.now(),
      };
    }
  } catch (e) {
    /* the note then shows without the piece */
  }
  // HB 2026-10-16 (Mini PDP item 4, Vic): a preorder piece carries its note on the order line, as the product page's
  // form does (properties[Preorder], snippets/add-to-cart-button); the note is the variant's own
  // (custom.some_text, data-preorder-note), without the "Preorder: " the button shows
  const item = { id, quantity: 1 };
  if (add.dataset.pre === '1') {
    const note = (add.dataset.preorderNote || add.dataset.preorder || '').replace(/^Preorder:\s*/i, '').trim();
    if (note) item.properties = { Preorder: note };
  }
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] }),
    });
    if (!res.ok) throw new Error('add ' + res.status);
    const data = await res.json();
    try {
      if (typeof window.triggerKlaviyoAddedToCart === 'function') window.triggerKlaviyoAddedToCart(data);
    } catch (e) {
      /* nothing */
    }
    track('hb_sheet_add', { handle: S.handle, variant: id });
    // The sheet closes; the quick add's own note rises and the cart stays closed (assets/hb-cart.js, source variant-popup-inner)
    close();
    setTimeout(() => {
      document.dispatchEvent(new CartUpdateEvent({ source: 'variant-popup-inner' }));
    }, SHEET_MS + 20);
  } catch (e) {
    add.classList.remove('is-busy');
    const tx = foot.querySelector('[data-hb-qv-add-tx]');
    if (tx) {
      const was = tx.textContent;
      tx.textContent = 'Not added, try again';
      setTimeout(() => {
        tx.textContent = was;
      }, 2500);
    }
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Size guide & info (QV round 2, fix 5.151, Vic: no "DETAILS" at the top; the x closes back to the Mini PDP, not to the
// collection; "just slide-up over the Mini PDP"). The product page's own tab sheet (assets/hb-pdp.css, .hb-sheet: the
// handle, DESCRIPTION / FIT / CARE / DELIVERY with the x on their line, as tall as its longest tab), rising over the
// quick view, which stays exactly as she left it under a dim. One layer at a time: its x, a swipe down, the dim, Esc
// and the phone's Back close this sheet only. The tabs come from sections/hb-qv-fit (the product page's details
// snippet in an inert template), one fetch per product per page, made ahead once the quick view is in.
const INFO_TABS = [
  ['description', 'Description'],
  ['fit', 'Fit'],
  ['care', 'Care'],
  ['delivery', 'Delivery'],
  ['styling', 'Styling'], // HB 2026-10-16: the draft from sections/hb-qv-fit (only where that product has pairs)
];
const infoCache = new Map();
function infoEntry(handle) {
  const hit = infoCache.get(handle);
  if (hit) return hit;
  const entry = { panels: null, p: null };
  entry.p = fetch('/products/' + encodeURIComponent(handle) + '?section_id=hb-qv-fit', { credentials: 'same-origin' })
    .then((res) => {
      if (!res.ok) throw new Error('fit ' + res.status);
      return res.text();
    })
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const tpl = doc.querySelector('[data-hb-qv-fit-src]');
      const root = tpl && tpl.content ? tpl.content : doc;
      const panels = [];
      INFO_TABS.forEach(([key, label]) => {
        const panel = root.querySelector('[data-hb-panel="' + key + '"]');
        if (panel && panel.innerHTML.trim()) panels.push({ key, label, html: panel.innerHTML });
      });
      entry.panels = panels;
      return panels;
    });
  entry.p.catch(() => {
    if (infoCache.get(handle) === entry) infoCache.delete(handle);
  });
  infoCache.set(handle, entry);
  if (infoCache.size > 30) infoCache.delete(infoCache.keys().next().value);
  return entry;
}

// HB 2026-10-16 (Mini PDP item 2, Vic: the Delivery tab's arrival date "is blank there today"): the product page's
// script fills its Arrives line from the page's own delivery estimate; here the cart drawer's (assets/
// hb-delivery-estimate.js, on every page) says the same dates, window.HBArrives ("Arrives Oct 19 – 20"). Not for a
// preorder piece (its own date is on the button and the order line), and nothing when the date is not known yet.
function fillArrival(qv, foot, panelsEl) {
  const row = panelsEl.querySelector('[data-hb-arrival-row]');
  const el = panelsEl.querySelector('[data-hb-arrival-long]');
  if (!row || !el) return;
  const add = foot && foot.querySelector('[data-hb-qv-add]');
  const a = String(window.HBArrives || '').replace(/^Arrives\s+/i, '').trim();
  const show = !!a && !(add && add.dataset.pre === '1');
  row.hidden = !show;
  if (show) el.textContent = a + ', when you order today';
}

const infoLayer = () => {
  const d = dlg();
  return d ? d.querySelector('[data-hb-qv-info]') : null;
};
const infoOpen = () => {
  const layer = infoLayer();
  return !!(layer && !layer.dataset.closing);
};
let infoEscAt = 0;

function showInfo(qv, foot, want, btn) {
  const d = dlg();
  const panel = q('[data-hb-sl-panel]', d);
  if (!S.open || !panel) return;
  const old = infoLayer();
  if (old) {
    if (!old.dataset.closing) return; // up already
    old.remove(); // still on its way down: the new one takes its place
  }
  const handle = S.handle;
  const entry = infoEntry(handle);
  aheadQuiet(1500); // its fit pictures first (QV round 4)
  const layer = document.createElement('div');
  layer.className = 'hb-qv-info';
  layer.setAttribute('data-hb-qv-info', '');
  layer.innerHTML =
    '<span class="hb-qv-info__dim" data-hb-qv-info-close></span>' +
    '<div class="hb-qv-info__sheet" role="dialog" aria-modal="true" aria-label="Size guide and info" tabindex="-1">' +
    '<div class="hb-qv-info__head" data-hb-qv-info-head><span class="hb-qv-info__handle" aria-hidden="true"></span>' +
    '<div class="hb-qv-info__bar"><div class="hb-qv-info__tabs" role="tablist" data-hb-qv-info-tabs></div>' +
    '<button type="button" class="hb-qv-info__x" data-hb-qv-info-close aria-label="Close"></button></div></div>' +
    '<div class="hb-qv-info__panels" data-hb-qv-info-panels></div>' +
    '</div>';
  const sheet = layer.querySelector('.hb-qv-info__sheet');
  const tabsEl = layer.querySelector('[data-hb-qv-info-tabs]');
  const panelsEl = layer.querySelector('[data-hb-qv-info-panels]');
  const note = (text) => '<div class="hb-qv-info__panel"><p class="hb-qv-info__empty">' + text + '</p></div>';
  const fill = (panels) => {
    if (!panels.length) {
      panelsEl.innerHTML = note('True to size. Not sure? Send your measurements to shop@hanne-bloch.com and we will help you.');
      return;
    }
    const first = panels.some((p) => p.key === want) ? want : panels[0].key;
    tabsEl.innerHTML = panels.map((p) => '<button type="button" class="hb-qv-info__tab" role="tab" data-hb-qv-tab="' + p.key + '" aria-selected="false">' + p.label + '</button>').join('');
    panelsEl.innerHTML = panels.map((p) => '<div class="hb-qv-info__panel" role="tabpanel" data-hb-qv-panel="' + p.key + '">' + p.html + '</div>').join('');
    panelsEl.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
    fillArrival(qv, foot, panelsEl);
    const show = (key) => {
      tabsEl.querySelectorAll('[data-hb-qv-tab]').forEach((t) => t.setAttribute('aria-selected', t.dataset.hbQvTab === key ? 'true' : 'false'));
      panelsEl.querySelectorAll('[data-hb-qv-panel]').forEach((p) => {
        const is = p.dataset.hbQvPanel === key;
        p.classList.toggle('is-hidden', !is);
        p.inert = !is;
        if (is) p.scrollTop = 0;
      });
    };
    tabsEl.querySelectorAll('[data-hb-qv-tab]').forEach((t) => {
      t.addEventListener('click', () => {
        show(t.dataset.hbQvTab);
        track('hb_sheet_fit_tab', { handle, tab: t.dataset.hbQvTab });
      });
    });
    show(first);
  };
  if (entry.panels) {
    fill(entry.panels);
  } else {
    // still on its way: the sheet rises at its full height and keeps it, so nothing jumps when the tabs come
    sheet.classList.add('is-full');
    panelsEl.innerHTML = note('One moment.');
    entry.p
      .then((panels) => {
        if (layer.isConnected && !layer.dataset.closing) fill(panels);
      })
      .catch(() => {
        if (layer.isConnected) panelsEl.innerHTML = note('The details could not be loaded. The product page has them.');
      });
  }
  panel.appendChild(layer); // in the sheet's panel, over its head, body and foot (it goes with the sheet, see init)
  // the quick view under it stays as she left it, out of reach until the sheet goes
  const footEl = foot.querySelector('.hb-qv__foot');
  qv.inert = true;
  if (footEl) footEl.inert = true;
  layer.hbBack = { qv, footEl, btn };
  wireInfo(layer, sheet, panelsEl);
  void sheet.offsetWidth; // its closed place first, so it rises
  layer.classList.add('is-open');
  try {
    sheet.focus({ preventScroll: true });
  } catch (e) {
    /* nothing */
  }
  track('hb_sheet_fit', { handle, tab: want || 'fit' });
}

function closeInfo() {
  const layer = infoLayer();
  if (!layer || layer.dataset.closing) return;
  layer.dataset.closing = '1';
  layer.classList.remove('is-open');
  const back = layer.hbBack || {};
  if (back.qv) back.qv.inert = false;
  if (back.footEl) back.footEl.inert = false;
  if (back.btn && back.btn.isConnected) {
    try {
      back.btn.focus({ preventScroll: true });
    } catch (e) {
      /* nothing */
    }
  }
  setTimeout(() => layer.remove(), 360);
}

function dropInfo() {
  const layer = infoLayer();
  if (!layer) return;
  layer.dataset.closing = '1';
  layer.remove();
}

function wireInfo(layer, sheet, panelsEl) {
  layer.addEventListener('click', (e) => {
    if (e.target instanceof Element && e.target.closest('[data-hb-qv-info-close]')) closeInfo();
  });
  // the quick view's own swipe down (assets/hb-sheet.js listens on the sheet's body) never starts from in here
  ['touchstart', 'touchend'].forEach((type) => layer.addEventListener(type, (e) => e.stopPropagation(), { passive: true }));
  // phone: the sheet follows a pull down on its head; past 70 px (or a quick flick) it goes, else it settles back
  const head = layer.querySelector('[data-hb-qv-info-head]');
  let drag = null;
  head.addEventListener('pointerdown', (e) => {
    if (!phone()) return;
    drag = { y: e.clientY, t: performance.now(), dy: 0, moved: false };
  });
  head.addEventListener('pointermove', (e) => {
    if (!drag) return;
    const dy = e.clientY - drag.y;
    if (!drag.moved) {
      if (dy < 6) return; // a tap on a tab or the x stays a tap
      drag.moved = true;
      try {
        head.setPointerCapture(e.pointerId);
      } catch (err) {
        /* nothing */
      }
      sheet.style.transition = 'none';
    }
    drag.dy = Math.max(0, dy);
    sheet.style.transform = 'translate3d(0,' + drag.dy + 'px,0)';
  });
  const end = () => {
    if (!drag) return;
    const { moved, dy, t } = drag;
    drag = null;
    if (!moved) return;
    sheet.style.transition = '';
    sheet.style.transform = '';
    if (dy > 70 || (dy > 30 && performance.now() - t < 250)) closeInfo();
  };
  head.addEventListener('pointerup', end);
  head.addEventListener('pointercancel', end);
  // a swipe down on the text when it is at its top
  let touch = null;
  const shown = () => panelsEl.querySelector('.hb-qv-info__panel:not(.is-hidden)');
  panelsEl.addEventListener('touchstart', (e) => {
    const p = shown();
    touch = (!p || p.scrollTop <= 0) && e.touches.length === 1 ? { y: e.touches[0].clientY, t: performance.now() } : null;
  }, { passive: true });
  panelsEl.addEventListener('touchend', (e) => {
    if (!touch) return;
    const dy = (e.changedTouches[0] ? e.changedTouches[0].clientY : touch.y) - touch.y;
    const dt = performance.now() - touch.t;
    touch = null;
    const p = shown();
    if ((!p || p.scrollTop <= 0) && dy > 90 && dt < 400) closeInfo();
  }, { passive: true });
}

// Esc and the dim around the desktop window close the Size guide & info sheet first, the quick view after
// (assets/hb-sheet.js closes the whole sheet on each; these run before it and stop it while this sheet is up)
function onInfoKey(e) {
  if (e.key !== 'Escape' || !infoOpen()) return;
  e.preventDefault();
  e.stopPropagation();
  infoEscAt = performance.now();
  closeInfo();
}
function onInfoCancel(e) {
  if (!infoOpen() && performance.now() - infoEscAt > 400) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  closeInfo();
}
function onInfoDim(e) {
  if (e.target !== e.currentTarget || !infoOpen()) return;
  e.stopImmediatePropagation();
  closeInfo();
}
// The phone's Back: assets/hb-sheet.js asks S.onBack() before it closes the sheet (QV round 2); while Size guide & info
// is up, the Back closes it and the quick view stays
function onInfoBack() {
  if (!infoOpen()) return false;
  closeInfo();
  // the sheet's own history entry was the one taken: it goes back, so the next Back closes the quick view
  if (S.hist) {
    try {
      history.pushState({ hbSheet: 1 }, '', location.href);
    } catch (err) {
      S.hist = false;
    }
  }
  return true;
}

// ---------------------------------------------------------------------------------------------------------------
// The opener on the cards

function onCardTap(e) {
  const d = dlg();
  if (!d) return;
  if (d.dataset.qv !== '1') return; // the sheet is on the page for the set builder alone
  if (!phone() && d.dataset.desktop !== '1') return;
  const t = e.target;
  if (!(t instanceof Element)) return;
  const card = t.closest('product-card');
  if (!card || !card.closest('.product-grid')) return;
  if (card.hasAttribute('data-no-navigation') || card.hasAttribute('data-placeholder')) return;
  if (t.closest('button, input, label, select, [tabindex="1"], card-swatch, .atc-icon-wrapper, .variant-popup, .variant-popup-inner, .custom-size-row, .atc-plus-icon')) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
  const link = card.querySelector('.product-card__link');
  if (!link || !link.getAttribute('href')) return;
  let url;
  try {
    url = new URL(link.href, location.href);
  } catch (err) {
    return;
  }
  const m = url.pathname.match(/\/products\/([^/?#]+)/);
  if (!m) return;
  if (S.open || d.open || blocked()) return; // (d.open: the last sheet is still on its way down)
  e.preventDefault();
  e.stopPropagation();
  openQuickView(decodeURIComponent(m[1]), url.pathname + url.search, card, link);
}

// Desktop: a card the mouse rests on for a moment is fetched ahead, so its quick view rises complete
let hoverCard = null;
let hoverT = 0;
function onCardHover(e) {
  if (e.pointerType !== 'mouse' || phone()) return;
  const card = e.target instanceof Element ? e.target.closest('product-card') : null;
  if (card === hoverCard) return;
  hoverCard = card;
  clearTimeout(hoverT);
  if (!card || !card.closest('.product-grid')) return;
  const d = dlg();
  if (!d || d.dataset.qv !== '1' || d.dataset.desktop !== '1') return;
  hoverT = setTimeout(() => {
    const handle = cardHandle(card);
    if (handle && S && !S.open && !readyText(handle)) qvText(handle).catch(() => {});
  }, 150);
}

// "Take me to the product": the sheet's history entry goes first (close pops it), then the page, so Back from the
// product page lands on the card (moved here from the sheet's popstate handler in assets/hb-sheet.js)
function onPop() {
  if (!S || !S.navTo) return;
  const u = S.navTo;
  S.navTo = null;
  rememberCard();
  location.href = u;
}

function init() {
  H = window.HBSheet;
  const d = dlg();
  if (!H || !d) return false;
  if (H.qv) return true; // wired already
  if (typeof H.openQuickView === 'function') return true; // an older assets/hb-sheet.js runs its own copy: stay out
  S = H.state;
  open = H.open;
  close = H.close;
  SHEET_MS = Number(H.SHEET_MS) || 350;
  if (typeof H.blocked === 'function') blocked = H.blocked;
  if (!S || typeof open !== 'function' || typeof close !== 'function') return true;
  H.openQuickView = openQuickView;
  H.qv = { version: QV_VERSION };
  document.addEventListener('click', onCardTap, true);
  document.addEventListener('pointerover', onCardHover, { passive: true });
  let fitT = 0;
  window.addEventListener('resize', () => {
    clearTimeout(fitT);
    fitT = setTimeout(dotsRelayout, 120); // the color row laid out again for the new width (QV round 4)
  });
  window.addEventListener('popstate', onPop);
  // Size guide & info first: the Back (the sheet's hook), Esc and the dim (capture: before the sheet's own listeners)
  S.onBack = onInfoBack;
  d.addEventListener('keydown', onInfoKey, true);
  d.addEventListener('cancel', onInfoCancel, true);
  d.addEventListener('click', onInfoDim, true);
  d.addEventListener('close', dropInfo); // the sheet is gone: so is this one (it lives in the sheet's panel)
  return true;
}

// assets/hb-sheet.js comes first on the page and sets window.HBSheet; should it be late, try again once the page is read
if (!init()) {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      if (!init()) setTimeout(init, 1500);
    },
    { once: true }
  );
}
